// Externe Bibliotheken
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

// Interne Module
import countryList from '@/_data/countryList';
import { getImgPath } from '@/_helper/imageHelper';

// Styles
import './phoneInput.scss';
import { Icon } from '@/comp/icon/icon.tsx';

const DEFAULT_MIN_LENGTH = 10;

class PhoneInput extends Component {
    constructor(props) {
        super(props);

        this.state = {
            dropdown: {
                active: false,
                scrollPos: 0,
            },
            mobileNumber: '',
            numberParts: null,
            phoneCountryCode: 'DE',
            phoneCountryPrefix: '+49',
            initialDataSet: false,
        };

        this.lists = {
            quickCountryList: {},
            prefixedCountryList: this.getPrefixedCountryList(countryList),
        };

        ['DE', 'AT', 'CH'].forEach((value) => {
            this.lists.quickCountryList[value] = countryList[value];
        });

        this.inputComponent = React.createRef();
        this.dropdownContent = React.createRef();
    }

    getPrefixedCountryList(countryList) {
        const prefixedCountryList = {};

        Object.keys(countryList).forEach((value) => {
            const item = countryList[value];

            if (item?.value !== undefined && item?.value?.length !== 0) {
                prefixedCountryList[value] = item;
            }
        });
        return prefixedCountryList;
    }

    componentDidMount() {
        this.handleOutsideClick(this.inputComponent);

        const phoneData = this.props.phoneData;
        // If initial PhoneData is available at mount time, set it
        if (phoneData) {
            this.setInitialPhoneData(phoneData);
        }
    }

    componentDidUpdate(prevProps) {
        // Check if phoneData was not available before but is available now
        // Only set initial data if it's coming from props and not from user input
        if (
            !prevProps.phoneData &&
            this.props.phoneData &&
            !this.state.initialDataSet &&
            this.state.mobileNumber === ''
        ) {
            this.setInitialPhoneData(this.props.phoneData);
        }
    }

    setInitialPhoneData(phoneData) {
        const newPhoneData = {};
        if (phoneData.phoneCountryPrefix) {
            newPhoneData['phoneCountryPrefix'] = phoneData.phoneCountryPrefix;

            const filteredEntries = Object.entries(countryList).filter(([_, countryData]) => {
                return countryData.value === newPhoneData['phoneCountryPrefix'];
            });
            newPhoneData['phoneCountryCode'] = filteredEntries.length > 0 ? filteredEntries[0][0] : null;
        }
        if (phoneData.phoneAreaCode && phoneData.phoneNumber) {
            newPhoneData['mobileNumber'] = phoneData.phoneAreaCode + phoneData.phoneNumber;
        }
        this.setState(
            {
                ...newPhoneData,
                initialDataSet: true,
            },
            () => this.validatePhoneNumber()
        );
    }

    resetTrigger() {
        return (
            this.state.mobileNumber.length > 0 && (
                <Icon
                    name="close"
                    className="reset-trigger"
                    role="button"
                    ariaLabel="eingabe zurücksetzen"
                    onClick={() => {
                        this.setState({ mobileNumber: '' }, () => {
                            this.validatePhoneNumber();
                        });
                    }}
                />
            )
        );
    }

    handleOutsideClick(ref) {
        document.addEventListener('mousedown', (event) => {
            if (ref.current && !ref.current.contains(event.target) && this.state.dropdown.active) {
                const dropdown = this.state.dropdown;
                dropdown.active = false;

                this.setState({ dropdown: dropdown });
            }
        });
    }

    countrySelect() {
        const toggleDropdown = () => {
            const dropdown = this.state.dropdown;
            dropdown.active = !dropdown.active;

            this.setState({ dropdown: dropdown }, () => {
                if (this.state.dropdown.active) {
                    // set scroll pos
                    if (this.dropdownContent.current !== null) {
                        this.dropdownContent.current.scrollTop = this.state.dropdown.scrollPos;
                    }
                }
            });
        };

        const selectByKey = (event) => {
            let dropdown = this.state.dropdown;
            let newElement;

            if (dropdown.active) {
                event.preventDefault();

                // Arrow Keys
                if (event.keyCode === 38) {
                    const element = this.dropdownContent.current.querySelectorAll('.active');
                    newElement = changeElement(element, 'up');
                } else if (event.keyCode === 40) {
                    const element = this.dropdownContent.current.querySelectorAll('.active');
                    newElement = changeElement(element, 'down');
                } else if (event.keyCode === 13) {
                    const element = this.dropdownContent.current.querySelector('.active');
                    setPhoneCountry(element.dataset.code);
                }

                // other Keys
                else {
                    newElement = getScrollElement(event.key);

                    if (newElement === undefined) {
                        return;
                    }
                }

                if (newElement !== null && newElement !== undefined) {
                    this.setState({
                        dropdown: {
                            active: true,
                            scrollPos: newElement.offsetTop,
                        },
                    });
                    this.dropdownContent.current.scrollTop = newElement.offsetTop;
                }
            }
        };

        const changeElement = (element, direction) => {
            let nextElement;

            if (direction === 'down') {
                nextElement = element[0].nextSibling;
            } else if (direction === 'up') {
                nextElement = element[0].previousSibling;
            }

            if (nextElement !== null) {
                element.forEach((elem) => {
                    elem.classList.remove('active');
                });
                nextElement.classList.add('active');

                return nextElement;
            }
        };

        const setPhoneCountry = (value) => {
            const dropdown = {
                active: false,
                scrollPos: this.dropdownContent.current.scrollTop,
            };

            this.setState(
                {
                    phoneCountryCode: value,
                    phoneCountryPrefix: this.lists.prefixedCountryList[value].value,
                    dropdown: dropdown,
                },
                () => {
                    this.validatePhoneNumber();
                }
            );
        };

        const getScrollElement = (key) => {
            if (key !== undefined) {
                let countryCode;

                for (let code in this.lists.prefixedCountryList) {
                    let country = this.lists.prefixedCountryList[code].text;
                    if (country[0].toLowerCase() === key) {
                        countryCode = code;
                        break;
                    }
                }
                if (countryCode !== undefined) {
                    return this.dropdownContent.current.querySelector(`[data-code=${countryCode}]`);
                }
            }
        };

        const mapCountryList = (list) => {
            return (
                list !== undefined &&
                Object.keys(list).map((value, index) => {
                    return (
                        <div
                            key={`${value}-${index}`}
                            className={`country-element ${
                                this.state.phoneCountryCode.toLowerCase() === value?.toLowerCase() ? 'active' : ''
                            } `}
                            data-code={value}
                            onClick={() => {
                                setPhoneCountry(value);
                            }}>
                            <img className="flag" src={getImgPath(value.toLowerCase())} alt={list[value].text ?? ''} />
                            {`${list[value].text} (${list[value].value})`}
                        </div>
                    );
                })
            );
        };

        return (
            <>
                <div className="current-country-code" tabIndex={0} onClick={toggleDropdown} onKeyDown={selectByKey}>
                    {this.state.phoneCountryCode}
                </div>

                {this.state.dropdown.active && (
                    <div className="dropdown-mobile-container">
                        <div className="dropdown">
                            <div className="dropdown-header">
                                Ländercode
                                <Icon
                                    name="close"
                                    size={20}
                                    ariaLabel="schließen"
                                    onClick={() => {
                                        toggleDropdown();
                                    }}
                                />
                            </div>
                            <div
                                className={`dropdown-content${this.props.showDACHOnly ? ' dach-only' : ''}`}
                                ref={this.dropdownContent}>
                                {mapCountryList(this.lists.quickCountryList)}
                                {!this.props.showDACHOnly && mapCountryList(this.lists.prefixedCountryList)}
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }

    handleUserInput(event) {
        const dropdown = this.state.dropdown;
        dropdown.active = false;

        this.setState({ dropdown: dropdown });
        this.validatePhoneNumber(event);
    }

    validatePhoneNumber(event) {
        const target = event?.target ?? null;
        let value = target?.value ?? this.state.mobileNumber;

        const prefixedCountryList = this.lists.prefixedCountryList;
        const listItem = prefixedCountryList[this.state.phoneCountryCode];

        const setNumberParts = (value) => {
            let numberParts = value && {
                phoneCountryCode: this.state.phoneCountryCode,
                phoneCountryPrefix: this.state.phoneCountryPrefix,
                phoneAreaCode: value.slice(0, 3),
                phoneNumber: value.slice(3, value.length),
                lengthValid: false,
                isError: false,
                isLandline: false,
            };

            // set as undefined if there is no value
            numberParts = value.length > 0 ? numberParts : undefined;

            // validate number length
            if (value.length >= countryList[this.state.phoneCountryCode]?.minLength ?? DEFAULT_MIN_LENGTH) {
                numberParts.lengthValid = true;
            }
            if (this.state.phoneCountryCode === 'DE' && Number(value[0]) !== 1 && numberParts?.isError === false) {
                numberParts.isError = true;
            }

            // check if number is landline
            if (listItem.whitelist !== undefined) {
                const shortestWhitelistItem = _.minBy(listItem.whitelist.includes, (obj) => obj.length).length;
                const longestWhitelistItem = _.maxBy(listItem.whitelist.includes, (obj) => obj.length).length;

                if (
                    value.length >= shortestWhitelistItem &&
                    !prefixedCountryList[this.state.phoneCountryCode].whitelist.includes.some((item) =>
                        value.slice(0, longestWhitelistItem).includes(item)
                    )
                ) {
                    numberParts.isLandline = true;
                }
            }

            this.setState({ numberParts: numberParts }, () => {
                // set number in parent component
                if (this.props.setMobileNumber !== undefined) {
                    this.props.setMobileNumber(numberParts);
                }
            });
        };

        const regex = /^[0-9_.\s]+$/g;
        const matchRegex = value.match(regex) !== null;

        // remove first 0
        value.slice(0, 1) === '0' && (value = value.slice(1));

        if (value.length === 0 || (matchRegex && value.length <= 12)) {
            this.setState({ mobileNumber: value }, () => {
                setNumberParts(this.state.mobileNumber);
            });
        }
    }

    render() {
        return (
            <div className="phone-input-component" ref={this.inputComponent}>
                {this.props.label !== undefined && (
                    <label className="input-label" htmlFor="mobile-number">
                        {this.props.label}
                    </label>
                )}
                <div className="input-container">
                    <div className={`dropdown-container ${this.props.withBorder ? 'border' : ''}`}>
                        {this.countrySelect()}
                    </div>

                    <div className={`input-selection ${this.props.withBorder ? 'border' : ''}`}>
                        <div className="current-prefix">{this.state.phoneCountryPrefix}</div>
                        <input
                            id="mobile-number"
                            name="mobile-number"
                            autoComplete="tel-national"
                            type="tel"
                            value={this.state.mobileNumber}
                            onChange={(event) => {
                                this.handleUserInput(event);
                            }}
                        />
                    </div>
                    {this.resetTrigger()}
                </div>
            </div>
        );
    }
}

export default PhoneInput;

PhoneInput.propTypes = {
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    setMobileNumber: PropTypes.func,
    phoneData: PropTypes.shape({
        phoneNumber: PropTypes.string,
        phoneCountryPrefix: PropTypes.string,
        phoneCountryCode: PropTypes.string,
        phoneAreaCode: PropTypes.string,
        isLandline: PropTypes.bool,
        lengthValid: PropTypes.bool,
        isError: PropTypes.bool,
    }),
};
